import { apiClient } from '@/utils/api-client'

/**
 * Target rule service for managing rules on targets.
 */
class TargetRuleService {
    /**
     * Create a new rule for a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @param {string} valueUlid - Value ULID
     * @param {string} description - Rule description
     * @param {Array} criteria - Rule criteria
     * @returns {Promise<Object>} Create response
     */
    async create(
        projectKey,
        environmentKey,
        targetUlid,
        valueUlid,
        description,
        criteria
    ) {
        const payload = {
            description: description,
            value_ulid: valueUlid,
            criteria: criteria,
        }

        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rules',
                payload
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Remove a rule from a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @param {string} ruleUlid - Rule ULID
     * @returns {Promise<Object>} Remove response
     */
    async remove(projectKey, environmentKey, targetUlid, ruleUlid) {
        return await apiClient
            .delete(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rules/' +
                    ruleUlid
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * List rules for a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @returns {Promise<Object>} Rules list response
     */
    async list(projectKey, environmentKey, targetUlid) {
        return await apiClient
            .get(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rules'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update a rule for a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} targetUlid - Target ULID
     * @param {string} ruleUlid - Rule ULID
     * @param {string} valueUlid - Value ULID
     * @param {string} description - Rule description
     * @param {Array} criteria - Rule criteria
     * @returns {Promise<Object>} Update response
     */
    async update(
        projectKey,
        environmentKey,
        targetUlid,
        ruleUlid,
        valueUlid,
        description,
        criteria
    ) {
        const payload = {
            description: description,
            value_ulid: valueUlid,
            criteria: criteria,
        }

        return await apiClient
            .put(
                '/projects/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/targets/' +
                    targetUlid +
                    '/rules/' +
                    ruleUlid,
                payload
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Reorder rules for a target.
     *
     * @param {string} projectKey - Project key
     * @param {string} flagKey - Flag key
     * @param {string} targetUlid - Target ULID
     * @param {Array} rules - Reordered rules
     * @returns {Promise<Object>} Reorder response
     */
    async reorder(projectKey, flagKey, targetUlid, rules) {
        const payload = {
            rules: rules,
        }

        return await apiClient
            .post(
                '/projects/' +
                    projectKey +
                    '/flags/' +
                    flagKey +
                    '/targets/' +
                    targetUlid +
                    '/rules-order',
                payload
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new TargetRuleService()
